<template>
  <DashboardDetailLayout
    title="Exports"
    subtitle="CSV export volume, per-user usage against 30-day limits, and daily totals."
    :sampleData="false"
  >
    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-1 mb-4">
      <MetricCard
        title="Companies Exported (24h)"
        :value="loadingSummary ? '—' : (summary.companies_exported_24h ?? 0)"
        subtitle="Sum across all exports today"
      />
      <MetricCard
        title="Companies Exported (30d)"
        :value="loadingSummary ? '—' : (summary.companies_exported_30d ?? 0)"
        subtitle="Total volume, last 30 days"
      />
      <MetricCard
        title="Active Exporters (30d)"
        :value="loadingSummary ? '—' : (summary.active_exporters_30d ?? 0)"
        subtitle="Distinct users"
      />
      <MetricCard
        title="Approaching Limit"
        :value="loadingSummary ? '—' : (summary.exporters_approaching_limit ?? 0)"
        subtitle="Users ≥ 80% of 30-day limit"
        valueTone="warn"
        borderTone="warn"
      />
      <MetricCard
        title="Largest Single Export (7d)"
        :value="loadingSummary ? '—' : (summary.largest_single_export_7d_count ?? 0)"
        :subtitle="summary.largest_single_export_7d_user || 'Last 7 days'"
      />
    </div>

    <div class="rounded-md border-2 border-gray-300 bg-neutral-50 shadow p-3 m-2">
      <div class="flex flex-row items-center justify-between mb-2">
        <div class="text-sm tracking-widest text-gray-700 font-poppins uppercase">
          Daily Company Exports — Last 30 Days
        </div>
        <div class="text-xs text-gray-500">
          <span v-if="loadingDetail">Loading…</span>
          <span v-else-if="detailErrorMsg" class="text-rose-600">{{ detailErrorMsg }}</span>
        </div>
      </div>
      <div class="h-64">
        <Bar :data="overallChartData" :options="overallChartOptions" />
      </div>
    </div>

    <div class="rounded-md border-2 border-gray-300 bg-neutral-50 shadow p-3 m-2">
      <div class="flex flex-row items-center justify-between mb-2">
        <div class="text-sm tracking-widest text-gray-700 font-poppins uppercase">
          Per-User Exports vs 30-Day Limit
        </div>
        <div class="text-xs text-gray-500">
          <span v-if="loadingDetail">Loading…</span>
          <span v-else-if="detail.users.length">{{ detail.users.length }} active exporter<span v-if="detail.users.length !== 1">s</span></span>
        </div>
      </div>

      <table class="w-full text-sm">
        <thead>
          <tr class="text-left text-gray-500 border-b">
            <th class="py-1">User</th>
            <th class="py-1 text-right">30d Total</th>
            <th class="py-1 text-right">Limit</th>
            <th class="py-1 text-right">% Used</th>
            <th class="py-1">Status</th>
            <th class="py-1">Daily Trend (30d)</th>
            <th class="py-1">Last Export</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!loadingDetail && detail.users.length === 0">
            <td colspan="7" class="py-4 text-center text-gray-500 italic">
              No exports in the last 30 days.
            </td>
          </tr>
          <tr v-for="u in detail.users" :key="u.user_id" class="border-b last:border-0">
            <td class="py-1">
              <div>{{ u.full_name }}</div>
              <div class="text-xs text-gray-500 font-mono">{{ u.username }}</div>
            </td>
            <td class="py-1 text-right font-mono">{{ formatNumbersWithCommas(u.thirty_day_unique_total) }}</td>
            <td class="py-1 text-right font-mono">{{ formatNumbersWithCommas(u.thirty_day_limit) }}</td>
            <td class="py-1 text-right font-mono">{{ u.percent_of_limit != null ? u.percent_of_limit.toFixed(1) + '%' : '—' }}</td>
            <td class="py-1">
              <span :class="['text-xs px-1.5 py-0.5 rounded-full', statusToneClass(u.percent_of_limit)]">
                {{ statusLabel(u.percent_of_limit) }}
              </span>
            </td>
            <td class="py-1">
              <SparklineChart :values="u.daily_series" :color="sparklineColor(u.percent_of_limit)" />
            </td>
            <td class="py-1 text-xs text-gray-700">{{ formatTimestamp(u.last_export_at) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </DashboardDetailLayout>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar } from 'vue-chartjs'
import DashboardDetailLayout from '../DashboardDetailLayout.vue'
import MetricCard from '../MetricCard.vue'
import SparklineChart from '../SparklineChart.vue'
import { formatNumbersWithCommas } from '@/composables/FormatNumbers'
import { getAdminDashboardData, getAdminDashboardExportDetail } from '@/services/Company'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

const store = useStore()
const loadingSummary = ref(true)
const loadingDetail = ref(true)
const detailErrorMsg = ref('')
const summary = ref({})
const detail = ref({ days: [], overall_daily_series: [], users: [] })

function statusLabel(pct) {
  if (pct == null) return '—'
  if (pct >= 100) return 'At/over limit'
  if (pct >= 80) return 'Approaching'
  return 'OK'
}

function statusToneClass(pct) {
  if (pct == null) return 'bg-gray-100 text-gray-700'
  if (pct >= 100) return 'bg-rose-100 text-rose-700'
  if (pct >= 80) return 'bg-amber-100 text-amber-800'
  return 'bg-emerald-100 text-emerald-700'
}

function sparklineColor(pct) {
  if (pct == null) return '#6B7280'
  if (pct >= 100) return '#E11D48'
  if (pct >= 80) return '#D97706'
  return '#3B82F6'
}

function formatTimestamp(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

const overallChartData = computed(() => {
  const labels = (detail.value.days || []).map(d => {
    const [, m, day] = d.split('-')
    return `${parseInt(m, 10)}/${parseInt(day, 10)}`
  })
  return {
    labels,
    datasets: [
      {
        label: 'Companies Exported',
        data: detail.value.overall_daily_series || [],
        backgroundColor: '#3B82F6',
      },
    ],
  }
})

const overallChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: { mode: 'index', intersect: false },
  },
  scales: {
    x: { grid: { display: false } },
    y: { beginAtZero: true, ticks: { precision: 0 }, grid: { color: 'rgba(0,0,0,0.05)' } },
  },
}

async function load() {
  const token = store.state.auth.token
  if (!token) {
    detailErrorMsg.value = 'Not authenticated.'
    loadingSummary.value = false
    loadingDetail.value = false
    return
  }
  try {
    summary.value = await getAdminDashboardData(token)
  } catch (e) {
    // non-fatal — detail page can still render with detail-only data
  } finally {
    loadingSummary.value = false
  }
  try {
    detail.value = await getAdminDashboardExportDetail(token)
  } catch (e) {
    const status = e && e.response && e.response.status
    detailErrorMsg.value = status ? `HTTP ${status}` : (e && e.message) || 'unknown error'
  } finally {
    loadingDetail.value = false
  }
}

load()
</script>
